import { useMemo, type ReactElement } from 'react';
import { Link } from '@tanstack/react-router';
import {
  useWorkoutsInRange,
  type WorkoutsResponse,
} from '@/lib/workouts';
import { useCalendarItems, type CalendarApiItem } from '@/lib/calendar';
import { DISCIPLINE_COLORS } from '@/lib/disciplineColors';
import { disciplineLabel } from '@/features/dashboard/WorkoutCard';
import { disciplineEmoji } from '@/features/dashboard/RecentActivitiesCard';
import {
  buildWeekGrid,
  dayKeyUTC,
  formatDayHeader,
  formatTime,
  isSameDayUTC,
  todayUTC,
} from './dateUtils';
import {
  buildCalendarItemsForDay,
  type CalendarItem,
} from './calendarItems';
import { useShowMealsPref } from './showMealsPref';

/**
 * Vista semanal: 7 columnas Lun..Dom. Cada columna es una pila vertical de
 * cards cronológicas. Footer con totales por día (horas + TSS).
 *
 * No usamos timeline real (no franjas horarias) — sólo cards ordenadas
 * cronológicamente; el evento principal de UX es ver "qué se hizo cada
 * día de la semana".
 */
export function WeekView({ cursor }: { cursor: Date }): ReactElement {
  const { fromISO, toISO, cells } = useMemo(() => buildWeekGrid(cursor), [cursor]);
  const query = useWorkoutsInRange(fromISO, toISO);
  const today = todayUTC();
  // M2.48.C — incluir meal/prep en la semana respetando el toggle global.
  const showMeals = useShowMealsPref();
  const calendarItemsQuery = useCalendarItems(fromISO, toISO, showMeals);

  return (
    <section aria-label="Vista semanal del calendario" data-testid="calendar-week-view">
      {query.isError && (
        <p role="alert" className="text-xs text-rose-700">
          No se pudieron cargar los entrenos de la semana.
        </p>
      )}

      <div
        className={`mt-1 grid grid-cols-1 gap-2 sm:grid-cols-7 sm:gap-1.5 ${
          query.isLoading ? 'opacity-60' : ''
        }`}
      >
        {cells.map((d) => (
          <WeekColumn
            key={dayKeyUTC(d)}
            date={d}
            isToday={isSameDayUTC(d, today)}
            workouts={query.data ?? null}
            mealsItems={showMeals ? (calendarItemsQuery.data?.items ?? null) : null}
          />
        ))}
      </div>
    </section>
  );
}

function WeekColumn({
  date,
  isToday,
  workouts,
  mealsItems,
}: {
  date: Date;
  isToday: boolean;
  workouts: WorkoutsResponse | null;
  /** Lista completa de calendar API items (meals/preps). Filtramos por día. */
  mealsItems: CalendarApiItem[] | null;
}): ReactElement {
  const dayKey = dayKeyUTC(date);
  // M2.29 — items unificados (planned + actual) con estado.
  const items = useMemo(
    () => buildCalendarItemsForDay(workouts, dayKey),
    [dayKey, workouts],
  );
  // M2.48.C — meals/preps de este día, ordenados por hora.
  const meals = useMemo(() => {
    if (!mealsItems) return [] as CalendarApiItem[];
    return mealsItems
      .filter((it) => it.kind === 'meal' || it.kind === 'prep')
      .filter((it) => it.datetime.slice(0, 10) === dayKey)
      .sort((a, b) =>
        a.datetime < b.datetime ? -1 : a.datetime > b.datetime ? 1 : 0,
      );
  }, [mealsItems, dayKey]);

  const totalMin = items.reduce((acc, w) => acc + w.durationMin, 0);
  const totalTss = items.reduce(
    (acc, w) => acc + (w.tss ?? 0),
    0,
  );
  const hours = totalMin / 60;

  return (
    <article
      data-testid={`calendar-week-col-${dayKey}`}
      className="flex flex-col rounded-md border border-slate-200 bg-white shadow-sm"
    >
      <header
        className={`flex items-center justify-between rounded-t-md border-b border-slate-200 px-2 py-1.5 text-xs ${
          isToday ? 'bg-teal-50 font-semibold text-teal-900' : 'bg-slate-50 text-slate-700'
        }`}
      >
        <span data-testid={`calendar-week-header-${dayKey}`}>
          {formatDayHeader(date)}
        </span>
        {isToday && (
          <span className="rounded-full bg-teal-600 px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-white">
            Hoy
          </span>
        )}
      </header>

      <div className="flex flex-1 flex-col gap-1.5 p-1.5">
        {items.length === 0 && meals.length === 0 ? (
          <p className="text-center text-[11px] text-slate-400">—</p>
        ) : (
          <>
            {items.map((item) => (
              <WeekActivityCard key={item.key} item={item} />
            ))}
            {meals.map((m) => (
              <WeekMealCard key={`m-${m.id}`} item={m} />
            ))}
          </>
        )}
      </div>

      {items.length > 0 && (
        <footer className="border-t border-slate-200 bg-slate-50 px-2 py-1 text-[10px] text-slate-600">
          <span data-testid={`calendar-week-totals-${dayKey}`}>
            {hours.toFixed(1)} h
            {totalTss > 0 ? ` · ${Math.round(totalTss)} TSS` : ''}
          </span>
        </footer>
      )}
    </article>
  );
}

/**
 * Card de UN item del día (M2.29). Pinta diferenciado por kind:
 *  - completed → ring verde + emoji ✓.
 *  - planned   → outline color disciplina (default existente).
 *  - skipped   → opacidad reducida + tachado + ✗.
 *  - partial   → ring ámbar + ½.
 *  - actual_unplanned → default (Strava sin planificar).
 */
function WeekActivityCard({ item }: { item: CalendarItem }): ReactElement {
  const color = DISCIPLINE_COLORS[item.discipline];
  const minutes = Math.round(item.durationMin);
  const km =
    typeof item.distanceM === 'number' && item.distanceM > 0
      ? item.distanceM / 1000
      : null;
  const tss = item.tss;
  const time = formatTime(item.datetime, item.timezone);

  const kindClasses = (() => {
    switch (item.kind) {
      case 'completed':
        return 'border-emerald-300 bg-emerald-50';
      case 'partial':
        return 'border-amber-300 bg-amber-50';
      case 'skipped':
        return 'border-slate-200 bg-slate-50 opacity-60';
      case 'planned':
        return 'border-slate-200 bg-white';
      case 'actual_unplanned':
      default:
        return 'border-slate-200 bg-white';
    }
  })();

  const statusBadge = (() => {
    if (item.kind === 'completed') return '✓ Hecho';
    if (item.kind === 'partial') return '½ Parcial';
    if (item.kind === 'skipped') return '✗ Saltado';
    if (item.kind === 'planned') return 'Planificado';
    return null;
  })();

  const innerContent = (
    <>
      <div className="flex items-center justify-between gap-1 text-slate-900">
        <span className="flex items-center gap-1 font-medium">
          <span aria-hidden="true">{disciplineEmoji(item.discipline)}</span>
          <span>{time}</span>
        </span>
        <span className="text-[10px] text-slate-500">{minutes} min</span>
      </div>
      {statusBadge && (
        <div className="mt-0.5 text-[9px] font-semibold uppercase tracking-wide text-slate-500">
          {statusBadge}
        </div>
      )}
      <div className="mt-0.5 text-slate-700">
        {km !== null && <span>{formatKm(km)} km</span>}
        {km !== null && (item.avgHr || tss !== null) && (
          <span aria-hidden="true"> · </span>
        )}
        {item.avgHr && <span>HR {item.avgHr}</span>}
        {item.avgHr && tss !== null && <span aria-hidden="true"> · </span>}
        {tss !== null && <span>TSS {Math.round(tss)}</span>}
      </div>
    </>
  );

  // Solo link al detalle si tiene actualId (planned puros no tienen vista
  // de detalle propia todavía).
  if (item.actualId !== null) {
    return (
      <Link
        to="/entreno/$id"
        params={{ id: String(item.actualId) }}
        data-testid={`calendar-week-card-${item.key}`}
        data-kind={item.kind}
        style={{ borderLeftColor: color.hex }}
        aria-label={`${disciplineLabel(item.discipline)}${statusBadge ? ` ${statusBadge.toLowerCase()}` : ''} a las ${time}, ${minutes} minutos${km !== null ? `, ${formatKm(km)} kilómetros` : ''}. Ver detalle`}
        className={`block rounded border border-l-4 p-1.5 text-[11px] shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-1 ${kindClasses}`}
      >
        {innerContent}
      </Link>
    );
  }

  // Planned puro — no link, solo card.
  return (
    <div
      data-testid={`calendar-week-card-${item.key}`}
      data-kind={item.kind}
      style={{ borderLeftColor: color.hex }}
      aria-label={`${disciplineLabel(item.discipline)} planificado a las ${time}, ${minutes} minutos`}
      className={`block rounded border border-l-4 p-1.5 text-[11px] shadow-sm ${kindClasses}`}
    >
      {innerContent}
    </div>
  );
}

function formatKm(km: number): string {
  if (km >= 100) return Math.round(km).toString();
  if (km >= 10) return km.toFixed(1);
  return km.toFixed(2);
}

/**
 * M2.48.C — Card compacta para meal/prep en WeekView. Mismo estilo color que
 * DayView (ámbar=meal, sky=prep) pero más densa por la columna estrecha.
 */
function WeekMealCard({ item }: { item: CalendarApiItem }): ReactElement {
  const isMeal = item.kind === 'meal';
  const time = item.datetime.slice(11, 16);
  const bg = isMeal
    ? 'border-amber-300 border-l-4 border-l-amber-500 bg-amber-50'
    : 'border-sky-300 border-l-4 border-l-sky-500 bg-sky-50';
  const text = isMeal ? 'text-amber-900' : 'text-sky-900';
  const emoji = isMeal ? '🍽' : '⏳';
  return (
    <div
      data-testid={`calendar-week-${item.kind}-${item.id}`}
      data-kind={item.kind}
      className={`rounded border ${bg} p-1.5 text-[11px] shadow-sm`}
    >
      <div className={`flex items-center justify-between ${text}`}>
        <span className="flex items-center gap-1 font-medium">
          <span aria-hidden="true">{emoji}</span>
          <span>{time}</span>
        </span>
        {item.kcal !== null && item.kcal !== undefined && (
          <span className="text-[10px] opacity-80">{item.kcal} kcal</span>
        )}
      </div>
      <div className={`mt-0.5 truncate ${text}`}>{item.title}</div>
    </div>
  );
}
